import { useEffect, useState } from 'react';
import { PosStore } from './offline/store.js';
import { AdminScreen } from './ui/AdminScreen.js';
import { PosScreen } from './ui/PosScreen.js';
import { RegisterScreen } from './ui/RegisterScreen.js';
import { ResetPasswordScreen } from './ui/ResetPasswordScreen.js';
import { SetupScreen } from './ui/SetupScreen.js';

type Phase = 'loading' | 'register' | 'setup' | 'pos' | 'error';

export function App(): React.ReactElement {
  const [phase, setPhase] = useState<Phase>('loading');
  const [store, setStore] = useState<PosStore | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(() => window.location.pathname.startsWith('/admin'));
  // `/reset` datang daripada pautan e-mel: ia tidak memerlukan peranti atau sesi.
  const [isReset] = useState(() => window.location.pathname.startsWith('/reset'));

  useEffect(() => {
    let cancelled = false;

    async function boot(): Promise<void> {
      try {
        const opened = await PosStore.open();
        const identity = await opened.getIdentity();
        if (cancelled) return;
        setStore(opened);
        if (identity !== undefined) {
          setPhase('pos');
          return;
        }
        // Belum ada peranti: `/daftar` bermaksud kedai baharu, selebihnya persediaan peranti.
        setPhase(window.location.pathname.startsWith('/daftar') ? 'register' : 'setup');
      } catch (caught) {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : 'IndexedDB tidak dapat dibuka');
        setPhase('error');
      }
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Navigasi.
   *
   * `/admin` TIDAK memerlukan peranti yang sudah dipasangkan. Pemilik mesti boleh membuka laporan
   * daripada mana-mana PC dengan log masuk sahaja — memaksa PC itu menjadi terminal POS dahulu
   * bermakna laporan hanya boleh dilihat dari kaunter.
   */
  function go(to: 'pos' | 'admin' | 'daftar'): void {
    window.history.pushState({}, '', to === 'admin' ? '/admin' : to === 'daftar' ? '/daftar' : '/');
    setIsAdmin(to === 'admin');
    if (to === 'daftar') setPhase('register');
    if (to === 'pos') setPhase('pos');
  }

  if (phase === 'loading') {
    return <div className="center">Memuatkan…</div>;
  }

  if (phase === 'error') {
    return (
      <div className="center">
        <h1>Tidak dapat dimulakan</h1>
        <p>{error}</p>
        <p className="muted">
          Pelayar yang menyekat storan (mod peribadi) tidak boleh menjalankan POS ini.
        </p>
      </div>
    );
  }

  if (store === null) return <div className="center">Memuatkan…</div>;

  if (isReset) {
    return (
      <ResetPasswordScreen
        onDone={() => {
          window.history.pushState({}, '', '/admin');
          window.location.reload();
        }}
      />
    );
  }

  // Back office didahulukan: ia bergantung pada sesi pemilik, bukan pada identiti peranti.
  if (isAdmin) {
    return (
      <AdminScreen
        store={store}
        onDone={() => {
          window.history.pushState({}, '', '/');
          setIsAdmin(false);
          void (async () => {
            const identity = await store.getIdentity();
            setPhase(identity === undefined ? 'setup' : 'pos');
          })();
        }}
      />
    );
  }

  if (phase === 'register') {
    return (
      <RegisterScreen
        store={store}
        onReady={() => {
          go('pos');
        }}
        onCancel={() => {
          window.history.pushState({}, '', '/');
          setPhase('setup');
        }}
      />
    );
  }

  if (phase === 'setup') {
    return (
      <SetupScreen
        store={store}
        onReady={() => setPhase('pos')}
        onRegister={() => go('daftar')}
        onAdmin={() => go('admin')}
      />
    );
  }

  return <PosScreen store={store} onReset={() => setPhase('setup')} onAdmin={() => go('admin')} />;
}
